import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import { isStaffRole } from '../lib/roles';
import { clearStoredTabs } from './TabsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Diagnostic beacon: whenever the client drops a stored session on its own, tell the server why (helps trace "forced logout").
function reportSessionLost(token, reason) {
  try {
    let payload = {};
    try { payload = JSON.parse(atob((token || '').split('.')[1])); } catch (e) { /* unreadable */ }
    const body = JSON.stringify({ reason, user_id: payload.user_id || null, email: payload.email || null, role: payload.role || null, iat: payload.iat || null, exp: payload.exp || null, path: window.location.pathname, ua: navigator.userAgent, at: new Date().toISOString() });
    if (navigator.sendBeacon) navigator.sendBeacon(`${API}/auth/session-lost`, new Blob([body], { type: 'application/json' }));
    else fetch(`${API}/auth/session-lost`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch (e) { /* never block logout */ }
}


export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [authChecked, setAuthChecked] = useState(false);
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(0);
  const [idleMode, setIdleMode] = useState('logout'); // 'logout' (customers) | 'work_lock' (staff) | 'none'
  // Work-area lock: staff stay logged in (Social keeps working) but must re-enter their password to
  // continue in Loans/Customers/Reports… after the admin-configured idle time.
  const [workLocked, setWorkLocked] = useState(() => sessionStorage.getItem('bbc_work_locked') === '1');
  const lockWork = useCallback(() => { sessionStorage.setItem('bbc_work_locked', '1'); setWorkLocked(true); }, []);
  const unlockWork = useCallback(async (password) => {
    const r = await fetch(`${API}/auth/verify-password`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}` }, body: JSON.stringify({ password }) });
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Incorrect password'); }
    sessionStorage.removeItem('bbc_work_locked'); setWorkLocked(false);
  }, []);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    // Skip if already checked and user is set
    if (authChecked && user) {
      setLoading(false);
      return;
    }
    
    // Skip if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Try cookie-based auth first
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setLoading(false);
        return;
      }
    } catch {
      // Cookie auth check failed, try token-based
    }

    // Try token-based auth
    if (token) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await fetch(`${API}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setAuthChecked(true);
        } else if (response.status === 401 || response.status === 403) {
          // Server-side rejection. Staff tokens last a year, so a 401 for a token that is not expired is unexpected:
          // re-check once after a moment (transient backend hiccup / restart) and only then sign the user out.
          let confirmed = response.status === 401;
          let recheck = null;
          if (confirmed) {
            await new Promise((r) => setTimeout(r, 2500));
            try {
              recheck = await fetch(`${API}/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } });
              if (recheck.ok) { setUser(await recheck.json()); setAuthChecked(true); confirmed = false; }
              else if (recheck.status !== 401) { decodeTokenFallback(); confirmed = false; }
            } catch (e) { decodeTokenFallback(); confirmed = false; }
          }
          if (confirmed) {
            console.log('Auth failed with status:', response.status);
            reportSessionLost(token, `auth_me_${response.status}_x2`);
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
          } else if (response.status === 403) {
            decodeTokenFallback();
          }
        } else {
          // For other errors (429/500, transient), keep the token AND
          // hydrate `user` from the JWT payload so route guards don't
          // wrongly treat a rate-limited/errored check as "logged out".
          console.warn('Auth check failed with status:', response.status, '- keeping token');
          decodeTokenFallback();
        }
      } catch (error) {
        // Network error or timeout - don't clear the token, just log it
        console.warn('Auth check network error (keeping token):', error.name);
        decodeTokenFallback();
      }
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  function decodeTokenFallback() {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp * 1000 > Date.now()) {
        // Token not expired, set basic user info
        setUser({
          user_id: payload.user_id,
          email: payload.email,
          role: payload.role
        });
      } else {
        // Token expired - clear it
        console.log('Token expired, clearing');
        reportSessionLost(token, 'jwt_expired');
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
    } catch {
      // Can't decode token - but don't clear on a transient failure
      // Just leave state as is and retry next time
      console.warn('Could not decode token, keeping current state');
    }
  }

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (identifier, password, otpDeliveryMethods = null) => {
    // Using XMLHttpRequest for more reliable error handling (known fetch API issue)
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/auth/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              
              // Check if OTP is required
              if (data.otp_required) {
                resolve({
                  otp_required: true,
                  otp_session_id: data.otp_session_id,
                  otp_delivery_methods: data.otp_delivery_methods,
                  masked_email: data.masked_email,
                  masked_phone: data.masked_phone,
                  available_methods: data.available_methods,
                  message: data.message,
                  trusted_devices_enabled: data.trusted_devices_enabled
                });
              } else {
                // No OTP - direct login
                localStorage.setItem('token', data.access_token);
                setToken(data.access_token);
                setUser(data.user);
                resolve({ otp_required: false, user: data.user });
              }
            } catch (parseError) {
              console.error('Login parse error:', parseError);
              reject(new Error('Invalid response from server. Please try again.'));
            }
          } else {
            // Handle error response
            try {
              const errorData = JSON.parse(xhr.responseText);
              // Provide user-friendly error messages
              let errorMessage = errorData.detail || 'Login failed';
              
              // Make common errors more user-friendly
              if (errorMessage.toLowerCase().includes('invalid') || 
                  errorMessage.toLowerCase().includes('incorrect') ||
                  errorMessage.toLowerCase().includes('not found')) {
                errorMessage = 'Invalid email/phone or password. Please check your credentials.';
              } else if (errorMessage.toLowerCase().includes('locked') || 
                         errorMessage.toLowerCase().includes('disabled')) {
                errorMessage = 'Your account has been locked. Please contact support.';
              } else if (errorMessage.toLowerCase().includes('pending')) {
                errorMessage = 'Your account is pending approval. Please wait for verification.';
              }
              
              reject(new Error(errorMessage));
            } catch {
              // Default error messages based on status code
              if (xhr.status === 401) {
                reject(new Error('Invalid email/phone or password. Please check your credentials.'));
              } else if (xhr.status === 403) {
                reject(new Error('Access denied. Your account may be locked or pending approval.'));
              } else if (xhr.status === 404) {
                reject(new Error('Account not found. Please check your email/phone or register.'));
              } else if (xhr.status >= 500) {
                reject(new Error('Server error. Please try again later.'));
              } else {
                reject(new Error('Login failed. Please try again.'));
              }
            }
          }
        }
      };
      
      xhr.onerror = function() {
        console.error('Login network error');
        reject(new Error('Network error. Please check your internet connection.'));
      };
      
      const body = { identifier, password };
      if (otpDeliveryMethods) {
        body.otp_delivery_methods = otpDeliveryMethods;
      }
      // Include device token if available (for trusted device skip)
      const deviceToken = localStorage.getItem('device_token');
      if (deviceToken) {
        body.device_token = deviceToken;
      }
      
      xhr.send(JSON.stringify(body));
    });
  };

  const verifyOtp = async (sessionId, otpCode, rememberDevice = false) => {
    // Using XMLHttpRequest for more reliable error handling (known fetch API issue)
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/auth/verify-otp`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              localStorage.setItem('token', data.access_token);
              setToken(data.access_token);
              setUser(data.user);
              
              // Store device token if provided (trusted device)
              if (data.device_token) {
                localStorage.setItem('device_token', data.device_token);
              }
              
              resolve({
                user: data.user,
                device_trusted: data.device_trusted || false
              });
            } catch (parseError) {
              console.error('OTP verify parse error:', parseError);
              reject(new Error('Invalid response from server. Please try again.'));
            }
          } else {
            // Handle error response
            try {
              const errorData = JSON.parse(xhr.responseText);
              let errorMessage = errorData.detail || 'OTP verification failed';
              
              // Make common errors more user-friendly
              if (errorMessage.toLowerCase().includes('invalid') || 
                  errorMessage.toLowerCase().includes('incorrect') ||
                  errorMessage.toLowerCase().includes('wrong')) {
                errorMessage = 'Invalid OTP code. Please check and try again.';
              } else if (errorMessage.toLowerCase().includes('expired')) {
                errorMessage = 'OTP has expired. Please request a new code.';
              } else if (errorMessage.toLowerCase().includes('attempts') || 
                         errorMessage.toLowerCase().includes('locked')) {
                errorMessage = 'Too many failed attempts. Please request a new code.';
              }
              
              reject(new Error(errorMessage));
            } catch {
              // Default error messages based on status code
              if (xhr.status === 400) {
                reject(new Error('Invalid OTP code. Please check and try again.'));
              } else if (xhr.status === 401) {
                reject(new Error('OTP verification failed. Please try again.'));
              } else if (xhr.status === 410) {
                reject(new Error('OTP has expired. Please request a new code.'));
              } else if (xhr.status >= 500) {
                reject(new Error('Server error. Please try again later.'));
              } else {
                reject(new Error('OTP verification failed. Please try again.'));
              }
            }
          }
        }
      };
      
      xhr.onerror = function() {
        console.error('OTP verify network error');
        reject(new Error('Network error. Please check your internet connection.'));
      };
      
      xhr.send(JSON.stringify({ 
        session_id: sessionId, 
        otp_code: otpCode,
        remember_device: rememberDevice
      }));
    });
  };

  const resendOtp = async (sessionId) => {
    // Using XMLHttpRequest for more reliable error handling (known fetch API issue)
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/auth/resend-otp`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } catch (parseError) {
              console.error('Resend OTP parse error:', parseError);
              // Even if parsing fails, consider it success if status was OK
              resolve({ success: true });
            }
          } else {
            // Handle error response
            try {
              const errorData = JSON.parse(xhr.responseText);
              let errorMessage = errorData.detail || 'Failed to resend OTP';
              
              // Make common errors more user-friendly
              if (errorMessage.toLowerCase().includes('wait') || 
                  errorMessage.toLowerCase().includes('too soon') ||
                  errorMessage.toLowerCase().includes('cooldown')) {
                errorMessage = 'Please wait before requesting another code.';
              } else if (errorMessage.toLowerCase().includes('expired') ||
                         errorMessage.toLowerCase().includes('invalid session')) {
                errorMessage = 'Session expired. Please login again.';
              } else if (errorMessage.toLowerCase().includes('limit') ||
                         errorMessage.toLowerCase().includes('maximum')) {
                errorMessage = 'Maximum resend attempts reached. Please try again later.';
              }
              
              reject(new Error(errorMessage));
            } catch {
              // Default error messages based on status code
              if (xhr.status === 429) {
                reject(new Error('Too many requests. Please wait before trying again.'));
              } else if (xhr.status === 400 || xhr.status === 404) {
                reject(new Error('Session expired. Please login again.'));
              } else if (xhr.status >= 500) {
                reject(new Error('Server error. Please try again later.'));
              } else {
                reject(new Error('Failed to resend OTP. Please try again.'));
              }
            }
          }
        }
      };
      
      xhr.onerror = function() {
        console.error('Resend OTP network error');
        reject(new Error('Network error. Please check your internet connection.'));
      };
      
      xhr.send(JSON.stringify({ session_id: sessionId }));
    });
  };

  const register = async (userData) => {
    // Using XMLHttpRequest for reliable mobile WebView compatibility
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/auth/register`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(data);
            } else {
              reject(new Error(data.detail || 'Registration failed. Please try again.'));
            }
          } catch (parseError) {
            reject(new Error(xhr.status >= 200 && xhr.status < 300 
              ? 'Invalid response from server.' 
              : 'Registration failed. Please try again.'));
          }
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error. Please check your internet connection and try again.'));
      };
      
      xhr.send(JSON.stringify(userData));
    });
  };

  const verifyRegistration = async (registrationId, code) => {
    // Using XMLHttpRequest for more reliable error handling (known fetch API issue)
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/auth/verify-registration`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              // Registration complete - save token and user
              localStorage.setItem('token', data.access_token);
              setToken(data.access_token);
              setUser(data.user);
              resolve(data.user);
            } catch (parseError) {
              console.error('Parse error:', parseError);
              reject(new Error('Invalid response from server. Please try again.'));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.detail || 'Verification failed. Please try again.'));
            } catch {
              reject(new Error('Verification failed. Please try again.'));
            }
          }
        }
      };
      
      xhr.onerror = function() {
        console.error('Verification network error');
        reject(new Error('Network error. Please check your internet connection and try again.'));
      };
      
      xhr.send(JSON.stringify({ registration_id: registrationId, code }));
    });
  };

  const resendVerificationCode = async (registrationId) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/auth/resend-verification`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              resolve({ success: true });
            }
          } else {
            try {
              const data = JSON.parse(xhr.responseText);
              reject(new Error(data.detail || 'Failed to resend code.'));
            } catch {
              reject(new Error('Failed to resend code. Please try again.'));
            }
          }
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error. Please check your internet connection.'));
      };
      
      xhr.send(JSON.stringify({ registration_id: registrationId }));
    });
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    const response = await fetch(`${API}/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId })
    });

    if (!response.ok) {
      throw new Error('Session exchange failed');
    }

    const userData = await response.json();
    setUser(userData);
    return userData;
  };

  const logout = useCallback(async (clearDeviceToken = false) => {
    try {
      // This device must stop receiving call/chat pushes once logged out
      let fcm = null;
      try { fcm = window.AndroidBridge && window.AndroidBridge.getFcmToken ? window.AndroidBridge.getFcmToken() : localStorage.getItem('fcm_token'); } catch (_) { /* noop */ }
      if (fcm && token) {
        await fetch(`${API}/notifications/fcm/unregister?token=${encodeURIComponent(fcm)}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      }
      try { if (window.AndroidBridge && window.AndroidBridge.setSession) window.AndroidBridge.setSession('', ''); } catch (_) { /* noop */ }
    } catch { /* noop */ }
    try {
      let devId = null; try { devId = window.AndroidBridge && window.AndroidBridge.getDeviceId ? window.AndroidBridge.getDeviceId() : localStorage.getItem('bbc_device_id'); } catch (_) { /* noop */ }
      await fetch(`${API}/auth/logout${devId ? `?device_id=${encodeURIComponent(devId)}` : ''}`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch {
      // Ignore logout errors
    }
    localStorage.removeItem('token');
    if (clearDeviceToken) {
      localStorage.removeItem('device_token');
    }
    clearStoredTabs(); // never leak the previous account's (possibly admin-only) open tabs
    setToken(null);
    setUser(null);
    setAuthChecked(false);
    setIdleTimeoutMinutes(0);
    navigate('/');
  }, [token, navigate]);

  // ====== IDLE-TIMEOUT AUTO LOGOUT ======
  // Fetch the admin-configured idle timeout (in minutes) for the current
  // user as soon as they are authenticated.  Customer users hit
  // customer_settings.session_timeout_minutes (default 30); staff hit
  // staff_settings.session_timeout_hours converted to minutes.
  // 0 disables the timer entirely.
  useEffect(() => {
    if (!user || !token) { setIdleTimeoutMinutes(0); return; }
    let cancelled = false;
    fetch(`${API}/admin/settings/session-timeout`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (cancelled || !d) return;
        const m = parseInt(d.session_timeout_minutes, 10);
        setIdleTimeoutMinutes(Number.isFinite(m) && m >= 0 ? m : 0);
        setIdleMode(d.mode || 'logout');
      })
      .catch(() => { /* leave at 0 — disabled */ });
    return () => { cancelled = true; };
  }, [user, token]);

  const onIdleTimeout = useCallback(() => {
    if (idleMode === 'work_lock') { lockWork(); return; }
    if (idleMode === 'none') return;
    // Persist any pending incoming-call info so the Login screen banner
    // can offer a "tap to log in & answer" flow.
    try {
      const callInfo = sessionStorage.getItem('bbc_active_incoming_call');
      if (callInfo) sessionStorage.setItem('bbc_pending_call_after_logout', callInfo);
    } catch (_) { /* noop */ }
    toast.info('Session expired due to inactivity. Please log in again.');
    logout();
  }, [logout, idleMode, lockWork]);

  useIdleTimeout(idleTimeoutMinutes, onIdleTimeout, !!(user && token));

  // Trusted Devices Management
  const getTrustedDevices = async () => {
    const response = await fetch(`${API}/auth/trusted-devices`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch trusted devices');
    }
    return await response.json();
  };

  const revokeTrustedDevice = async (deviceId) => {
    const response = await fetch(`${API}/auth/trusted-devices/${deviceId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      throw new Error('Failed to revoke device');
    }
    return await response.json();
  };

  const revokeAllTrustedDevices = async () => {
    const response = await fetch(`${API}/auth/trusted-devices`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      throw new Error('Failed to revoke devices');
    }
    // Clear local device token as well
    localStorage.removeItem('device_token');
    return await response.json();
  };

  const getAuthHeaders = useCallback(() => {
    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  }, [token]);

  const isStaff = () => {
    return isStaffRole(user?.role);
  };

  const hasRole = (minRole) => {
    const roles = {
      admin: 100,
      regional_manager: 80,
      branch_manager: 60,
      relationship_manager: 60,
      loan_officer: 40,
      cashier: 20,
      customer: 10
    };
    return user && roles[user.role] >= roles[minRole];
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      token,
      workLocked,
      unlockWork,
      idleTimeoutMinutes,
      login,
      verifyOtp,
      resendOtp,
      register,
      verifyRegistration,
      resendVerificationCode,
      loginWithGoogle,
      exchangeSession,
      logout,
      getAuthHeaders,
      isStaff,
      hasRole,
      checkAuth,
      getTrustedDevices,
      revokeTrustedDevice,
      revokeAllTrustedDevices
    }}>
      {children}
    </AuthContext.Provider>
  );
};
